import { defineStore, acceptHMRUpdate } from "pinia";
import { brogglBack } from "../axios.js";
import { teamMembersReportTypes } from "../assets/variables.js";

export const useTeamMembersReportStore = defineStore("teamMembersReport", {
  state: () => ({
    teams: [],
    total: 0,
    chart: {},
    barChartKey: 0,
    empty: false,
    chartData: {
      labels: [],
      datasets: [],
    },
    parameters: {
      team: "",
      period: "Last Quarter",
      teamMembersReportType: teamMembersReportTypes[0].value,
    },
    totals: {},
    parametersTSOC: {
      period: "This Quarter",
    },
    timeSpentOnClient: [],
  }),
  getters: {
    getTeams: (state) => state.teams,
    getTotal: (state) => state.total,
    getChart: (state) => state.chart,
    getParameters: (state) => state.parameters,
    getBarChartKey: (state) => state.barChartKey,
    getChartData: (state) => state.chartData,
    getEmpty: (state) => state.empty,
    getTotals: (state) => state.totals,
    getTimeSpentOnClient: (state) => state.timeSpentOnClient,
  },
  actions: {
    async fetchTeams() {
      const res = await brogglBack.get("/team/teams", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
      });
      this.teams = res.data.data;
      this.parameters.team = this.teams[0].ID;
    },
    async getReport() {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      if (this.parameters.teamMembersReportType === "project") {
        const res = await brogglBack.get("/report/team-members/project-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            teamId: this.parameters.team,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        let resData = res.data.data.sort((a, b) => (a.name > b.name ? 1 : -1));
        for (const item of resData) {
          this.total += item.sumAllProjects;
          sumCS.push(item.sumCS);
          sumPS.push(item.sumPS);
          sumOnBoarding.push(item.sumOnBoarding);
          sumOtherProjects.push(item.sumOtherProjects);
        }
        this.total = this.total / 8;
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = resData.map((item) => item.name);
        this.chart = resData;
      } else {
        const res = await brogglBack.get("/report/team-members/billable-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            teamId: this.parameters.team,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        let resData = res.data.data.sort((a, b) => (a.name > b.name ? 1 : -1));
        for (let i = 0; i < resData.length; i++) {
          this.total += resData[i].sumAll;
          sumBillable.push(resData[i].sumBillable);
          sumNonBillable.push(resData[i].sumNonBillable);
          sumInternal.push(resData[i].sumInternal);
        }
        this.total = this.total / 8;
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });
        this.chartData.labels = resData.map((item) => item.name);
        this.chart = resData;
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
    },
    calculateTotals() {
      this.totals = {};
      if (this.parameters.teamMembersReportType === "project") {
        this.totals.CS = this.chart.reduce((acc, curr) => acc + curr.sumCS, 0);
        this.totals.PS = this.chart.reduce((acc, curr) => acc + curr.sumPS, 0);
        this.totals.OnBoarding = this.chart.reduce(
          (acc, curr) => acc + curr.sumOnboarding,
          0,
        );
        this.totals.OtherProjects = this.chart.reduce(
          (acc, curr) => acc + curr.sumOtherProjects,
          0,
        );
      } else {
        this.totals.Billable = this.chart.reduce(
          (acc, curr) => acc + curr.sumBillable,
          0,
        );
        this.totals.NonBillable = this.chart.reduce(
          (acc, curr) => acc + curr.sumNonBillable,
          0,
        );
        this.totals.Internal = this.chart.reduce(
          (acc, curr) => acc + curr.sumInternal,
          0,
        );
      }
    },
    convertArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      result += Object.keys(data[0]).join(columnDelimiter) + lineDelimiter;
      data.forEach(function (item) {
        Object.keys(item).forEach((key) => {
          result += item[key] + columnDelimiter;
        });
        result += lineDelimiter;
      });
      return result;
    },
    csvExport(arrData) {
      var data, filename, link;
      var csv = this.convertArrayOfObjectsToCSV(arrData);
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    async fetchReportDetails() {
      const reportType = this.parameters.teamMembersReportType;
      const teamId = this.parameters.team;
      const period = this.parameters.period;
      const endpoint =
        reportType === "project"
          ? "/report/team-members/project-report/csv"
          : "/report/team-members/billable-report/csv";

      try {
        const res = await brogglBack.get(endpoint, {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: { teamId, period },
          responseType: "blob",
        });

        const disposition = res.headers["content-disposition"] || "";
        const match = disposition.match(
          /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i,
        );
        let filename = `team-${teamId}-${reportType}-report.csv`;
        if (match) {
          try {
            filename = decodeURIComponent(match[1]);
          } catch {
            filename = match[1];
          }
        }

        const url = URL.createObjectURL(res.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(error);
      }
    },
    async getTimeSpendOnClient() {
      try {
        const res = await brogglBack.get(
          "/report/team-members/time-spent-by-client",
          {
            headers: {
              Authorization: "Bearer " + window.$cookies.get("jwt"),
            },
            params: { period: this.parametersTSOC.period },
          },
        );
        this.timeSpentOnClient = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    exportTimeSpendOnClientToCSV(arrData) {
      var data, filename, link;
      var csv = this.convertTimeSpendOnClientdArrayOfObjectsToCSV({
        data: arrData,
      });
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    convertTimeSpendOnClientdArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      result +=
        "Name" +
        columnDelimiter +
        "Role" +
        columnDelimiter +
        "Country" +
        columnDelimiter +
        "Pod" +
        columnDelimiter +
        "Sum All" +
        columnDelimiter +
        "Sum Billable" +
        columnDelimiter +
        "Sum Internal" +
        columnDelimiter +
        "Sum Non Billable" +
        columnDelimiter +
        "Client" +
        columnDelimiter +
        "Number of Business Days" +
        columnDelimiter +
        "Days Off (hour)" +
        columnDelimiter +
        "Days Off (day)" +
        columnDelimiter +
        "Average Time spent on clients (week)" +
        lineDelimiter;
      data.forEach((eleme) => {
        Object.keys(eleme).forEach((key) => {
          if (key !== "TeamId") {
            result += eleme[key] + columnDelimiter;
          }
        });
        result += lineDelimiter;
      });
      return result;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useTeamMembersReportStore, import.meta.hot),
  );
}
